import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { LOCAL_STORAGE_CONSTANT } from '../../../constants/app';
import { extractJSON } from '../../../services/jsonUtil';
import { doPaymentInitiationRequest } from '../../../store/action/products';
import { SEGMENT_EVENTS, trackEvent } from '../../../utils/Segment';
import { formatCurrency } from '../../../utils/helpers';
import { encodeBase64Value } from '../../../utils/url';
import { APP_ROUTES } from '../../../route';
import { FT_IMAGES } from './assets';

/**
 * For horizontal swipe rows (mobile): on mount, scroll so the `index`-th
 * `[data-ftl-slide]` child sits in the middle. No-op when the row isn't a
 * scroll container at the current breakpoint.
 */
export const useCenteredScroll = index => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !['auto', 'scroll'].includes(getComputedStyle(el).overflowX)) {
      return;
    }
    const item = el.querySelectorAll('[data-ftl-slide]')[index];
    if (!item) return;
    const elBox = el.getBoundingClientRect();
    const box = item.getBoundingClientRect();
    el.scrollLeft += box.left - elBox.left - (el.clientWidth - box.width) / 2;
  }, [index]);
  return ref;
};

/** Wheel + mouse drag for horizontal overflow-x rows. */
export const useHorizontalWheelScroll = () => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const canScrollHorizontally = () => el.scrollWidth > el.clientWidth + 1;

    const onWheel = event => {
      if (!canScrollHorizontally()) return;

      const { deltaX, deltaY } = event;
      const dominantVertical = Math.abs(deltaY) >= Math.abs(deltaX);
      if (!dominantVertical && deltaX !== 0) return;

      const maxScroll = el.scrollWidth - el.clientWidth;
      const nextScroll = el.scrollLeft + deltaY;
      const canScrollLeft = nextScroll > 0;
      const canScrollRight = nextScroll < maxScroll;

      if (!canScrollLeft && !canScrollRight) return;
      if ((deltaY < 0 && !canScrollLeft) || (deltaY > 0 && !canScrollRight)) {
        return;
      }

      event.preventDefault();
      el.scrollLeft = nextScroll;
    };

    let isDragging = false;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let activePointerId = null;

    const endDrag = event => {
      if (!isDragging) return;
      isDragging = false;
      activePointerId = null;
      el.classList.remove('ftl-areas-viewport--dragging');
      if (event?.pointerId != null && el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
    };

    const onPointerDown = event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!canScrollHorizontally()) return;

      isDragging = true;
      activePointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScrollLeft = el.scrollLeft;
      el.classList.add('ftl-areas-viewport--dragging');
      el.setPointerCapture(event.pointerId);
    };

    const onPointerMove = event => {
      if (!isDragging || event.pointerId !== activePointerId) return;
      event.preventDefault();
      el.scrollLeft = dragStartScrollLeft - (event.clientX - dragStartX);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('lostpointercapture', endDrag);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('lostpointercapture', endDrag);
      el.classList.remove('ftl-areas-viewport--dragging');
    };
  }, []);

  return ref;
};

const clampLineProgress = value => Math.min(1, Math.max(0, value));

const getReasonsScrollTargets = node => {
  const targets = new Set();
  if (typeof window !== 'undefined') {
    targets.add(window);
    targets.add(document.documentElement);
  }
  const app = document.getElementById('app');
  if (app) targets.add(app);
  document
    .querySelectorAll('.payment-page.main, #main-body')
    .forEach(el => targets.add(el));

  let parent = node?.parentElement;
  while (parent) {
    const { overflowY, overflowX } = getComputedStyle(parent);
    if (
      /(auto|scroll|overlay)/.test(overflowY) &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      targets.add(parent);
    }
    if (
      /(auto|scroll|overlay)/.test(overflowX) &&
      parent.scrollWidth > parent.clientWidth + 1
    ) {
      targets.add(parent);
    }
    parent = parent.parentElement;
  }

  return [...targets];
};

const updateReasonMarkers = (
  markers,
  anchorPosition,
  listRect,
  isHorizontal,
) => {
  let lastActiveIndex = -1;

  markers.forEach((marker, index) => {
    const rect = marker.getBoundingClientRect();
    const center = isHorizontal
      ? rect.left + rect.width / 2 - listRect.left
      : rect.top + rect.height / 2 - listRect.top;
    const isActive = anchorPosition >= center;

    marker.classList.toggle('ftl-reason__marker--active', isActive);
    if (isActive) lastActiveIndex = index;
  });

  markers.forEach((marker, index) => {
    marker.classList.toggle(
      'ftl-reason__marker--current',
      index === lastActiveIndex,
    );
  });
};

/** Drives the reasons timeline rail via CSS vars on the list element. */
export const useReasonsLineProgress = () => {
  const listRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const list = listRef.current;
    const scroller = scrollerRef.current;
    if (!list || !scroller) return undefined;

    const mobileQuery = window.matchMedia('(max-width: 767px)');
    let frame = 0;

    const update = () => {
      frame = 0;
      const markers = list.querySelectorAll('.ftl-reason__marker');
      const firstMarker = markers[0];
      const lastMarker = markers[markers.length - 1];
      if (!firstMarker || !lastMarker) return;

      const listRect = list.getBoundingClientRect();
      const firstRect = firstMarker.getBoundingClientRect();
      const lastRect = lastMarker.getBoundingClientRect();

      if (mobileQuery.matches) {
        const lineStart = firstRect.left + firstRect.width / 2 - listRect.left;
        const lineEnd = lastRect.left + lastRect.width / 2 - listRect.left;
        const lineLength = Math.max(0, lineEnd - lineStart);

        list.style.setProperty('--ftl-reasons-line-left', `${lineStart}px`);
        list.style.setProperty('--ftl-reasons-line-length', `${lineLength}px`);
        list.style.removeProperty('--ftl-reasons-line-top');

        const scrollerRect = scroller.getBoundingClientRect();
        const anchorX = scrollerRect.left + scroller.clientWidth * 0.5;
        const anchorInList = anchorX - listRect.left;
        const progress =
          lineLength <= 0
            ? 0
            : clampLineProgress((anchorInList - lineStart) / lineLength);

        list.style.setProperty('--ftl-reasons-line-progress', String(progress));
        updateReasonMarkers(markers, anchorInList, listRect, true);
        return;
      }

      const lineStart = firstRect.top + firstRect.height / 2 - listRect.top;
      const lineEnd = lastRect.top + lastRect.height / 2 - listRect.top;
      const lineLength = Math.max(0, lineEnd - lineStart);

      list.style.setProperty('--ftl-reasons-line-top', `${lineStart}px`);
      list.style.setProperty('--ftl-reasons-line-length', `${lineLength}px`);
      list.style.removeProperty('--ftl-reasons-line-left');

      const anchorY = window.innerHeight * 0.5;
      const anchorInList = anchorY - listRect.top;
      const progress =
        lineLength <= 0
          ? 0
          : clampLineProgress((anchorInList - lineStart) / lineLength);

      list.style.setProperty('--ftl-reasons-line-progress', String(progress));
      updateReasonMarkers(markers, anchorInList, listRect, false);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    const scrollTargets = getReasonsScrollTargets(list);
    scrollTargets.forEach(target => {
      target.addEventListener('scroll', scheduleUpdate, { passive: true });
    });
    scroller.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    mobileQuery.addEventListener('change', scheduleUpdate);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(list);
      resizeObserver.observe(scroller);
    }

    scheduleUpdate();

    return () => {
      scrollTargets.forEach(target => {
        target.removeEventListener('scroll', scheduleUpdate);
      });
      scroller.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      mobileQuery.removeEventListener('change', scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
    };
  }, []);

  return { listRef, scrollerRef };
};

/**
 * Same behaviour as containers/StartFreeTrial: guests go through the auth
 * flow (`onGuest`), signed-in users start checkout for the selected plan.
 */
export const useTrialCta = loading => {
  const dispatch = useDispatch();
  const { selectedPlan } = useSelector(state => state.product);
  const { payment } = useSelector(state => state.user);
  const user = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
  const isRejoin = Boolean(
    payment?.attributes?.ends_at &&
      new Date() > new Date(payment?.attributes?.ends_at),
  );
  const isDisabled = Boolean(
    user &&
      (!selectedPlan ||
        loading ||
        (!isRejoin && selectedPlan?.id === payment?.attributes?.product_id)),
  );

  const start = onGuest => {
    if (!user) {
      onGuest?.();
      return;
    }
    trackEvent(
      isRejoin
        ? SEGMENT_EVENTS.REJOIN_MEMBERSHIP
        : SEGMENT_EVENTS.START_FREE_TRIAL,
      {
        user_id: user?.id,
        subscription_duration: selectedPlan?.attributes?.interval,
        free_trial_days: selectedPlan?.attributes?.free_trial_days,
        subscription_amount: formatCurrency(
          Number(selectedPlan?.attributes?.amount_in_cents || 0) / 100,
          selectedPlan?.attributes?.currency,
        ),
        plan_name: selectedPlan?.attributes?.name,
        plan_id: selectedPlan?.id,
      },
    );
    dispatch(
      doPaymentInitiationRequest({
        page: encodeBase64Value(APP_ROUTES.FREE_TRAIL),
      }),
    );
  };

  return { start, isDisabled, isRejoin };
};

export const CtaButton = ({
  label,
  onStart,
  loading = false,
  variant = 'primary',
  withArrow = false,
  className,
}) => {
  const { start, isDisabled, isRejoin } = useTrialCta(loading);
  return (
    <button
      type="button"
      className={clsx('ftl-btn', `ftl-btn--${variant}`, className)}
      disabled={isDisabled}
      onClick={() => start(onStart)}
    >
      <span>{isRejoin ? 'Rejoin Membership' : label}</span>
      {withArrow && (
        <img
          className="ftl-btn__arrow"
          src={FT_IMAGES.arrowRight}
          alt=""
          width="16"
          height="16"
        />
      )}
    </button>
  );
};

CtaButton.propTypes = {
  label: PropTypes.string.isRequired,
  onStart: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['primary', 'white']),
  withArrow: PropTypes.bool,
  className: PropTypes.string,
};

export const Pill = ({ children, light = false }) => (
  <span className={clsx('ftl-pill', light && 'ftl-pill--light')}>
    <span className="ftl-pill__dot" />
    {children}
  </span>
);

Pill.propTypes = {
  children: PropTypes.node.isRequired,
  light: PropTypes.bool,
};

const phoneScreenMaskStyle = {
  WebkitMaskImage: `url("${FT_IMAGES.phoneScreenMask}")`,
  maskImage: `url("${FT_IMAGES.phoneScreenMask}")`,
};

/** iPhone frame with the app screen masked underneath, as in the Figma mockups. */
export const PhoneMockup = ({ screen, video, className, alt = '' }) => {
  const rootRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const el = videoRef.current;
    if (!video || !root || !el) return undefined;

    let visible = false;
    let playing = false;

    const start = () => {
      if (!visible || playing) return;
      el.muted = true;
      el.play()
        .then(() => {
          playing = true;
          observer.disconnect();
          el.removeEventListener('canplay', start);
        })
        .catch(() => {});
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (!visible) return;
        if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) start();
        else el.addEventListener('canplay', start);
      },
      { threshold: 0.5 },
    );

    observer.observe(root);
    return () => {
      observer.disconnect();
      el.removeEventListener('canplay', start);
    };
  }, [video]);

  return (
    <div ref={rootRef} className={clsx('ftl-phone', className)}>
      {video ? (
        <video
          ref={videoRef}
          className="ftl-phone__screen"
          src={video}
          muted
          loop
          playsInline
          preload="auto"
          aria-label={alt}
          style={phoneScreenMaskStyle}
        />
      ) : (
        <img
          className="ftl-phone__screen"
          src={screen}
          alt={alt}
          style={phoneScreenMaskStyle}
        />
      )}
      <img className="ftl-phone__frame" src={FT_IMAGES.phoneFrame} alt="" />
    </div>
  );
};

PhoneMockup.propTypes = {
  screen: PropTypes.string,
  video: PropTypes.string,
  className: PropTypes.string,
  alt: PropTypes.string,
};
